import React, { useEffect, useRef } from 'react';
import {
  preloadParallax, drawParallax, drawRooftop, drawDeckReflection, stripPosition,
} from './parallax';

/**
 * CityBackdrop — the game's layered city, alive behind a menu screen.
 *
 * The same parallax stack the arena is drawn over (parallax.js), seen from the
 * rooftop: the skyline drifting slowly along as if the view were travelling
 * across Sector 07, each layer at its own depth, the searchlights and the air
 * traffic running, the wet deck reflecting it — and the whole stack leaning a
 * little toward the pointer, so the depth answers the player's hand. Nothing
 * here is new art; it is the arena's own renderer with a camera that drifts.
 *
 * Fills its positioned parent. Decorative: hidden from assistive tech, inert
 * to clicks, and still (one frame, no drift) for anyone who has asked their
 * system for reduced motion.
 */

/** How fast the view travels along the skyline, in screen-widths a second. */
const DRIFT = 0.06;

/**
 * How the city is framed, by the screen's shape. `scale` is the play-area
 * height the layers are sized against, as a share of the backdrop's height —
 * the city's size (the arena uses its 600px play height times its zoom; a
 * full-screen backdrop is bigger than any arena). `ground` is where the
 * rooftop's far edge sits, as a share of the height.
 *
 * Wide, the skyline fills the screen behind a panel in the middle. Upright
 * (a phone), that composition put the panel squarely over every lit building
 * and left only dark sky and dark deck in view — the city was there and
 * nobody could see it. So upright the skyline is smaller and stands in the
 * top half, in full view, and the lobby's panel sits on the roof below it
 * (App.css, .game-container on a portrait screen).
 */
const FRAMING = {
  wide: { scale: 0.86, ground: 0.8 },
  upright: { scale: 0.5, ground: 0.42 },
};

/**
 * Eye level above the roof, in play-heights: a standing rider's head height,
 * the same eye the arena's perspective is built on (HORIZON_Y).
 */
const EYE_HEIGHT = 52 / 600;

/** Plating seam spacing on the deck, in play-heights. */
const SEAM_STEP = 96 / 600;

/** Canvas resolution cap: a backdrop does not need a 3x phone's full pixels. */
const MAX_DPR = 1.5;

const CityBackdrop = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext('2d');
    if (!ctx) return undefined;
    preloadParallax();

    const still = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The pointer's x as a share of the width, eased, for the sway.
    const lean = { target: 0.5, now: 0.5 };
    const onPointer = (e) => { lean.target = e.clientX / window.innerWidth; };
    window.addEventListener('pointermove', onPointer);

    let frame = null;
    const start = performance.now();
    const draw = (t) => {
      // Size to the element every frame: cheap, and it follows rotation and
      // resizing with no listener of its own.
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const w = Math.round(canvas.clientWidth * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      const seconds = still ? 0 : (t - start) / 1000;
      lean.now += (lean.target - lean.now) * 0.04;
      const screen = seconds * DRIFT;
      const midX = lean.now * w;
      const framing = h > w * 1.1 ? FRAMING.upright : FRAMING.wide;
      const playH = h * framing.scale;
      const groundY = h * framing.ground;
      const horizonY = groundY - playH * EYE_HEIGHT;

      // The painted city is soft art, unlike the arena's pixel sprites.
      ctx.imageSmoothingEnabled = true;
      if (!drawParallax(ctx, screen, midX, w, h, groundY, playH, horizonY, seconds)) {
        ctx.fillStyle = '#0b0f1e'; // until the layers arrive
        ctx.fillRect(0, 0, w, h);
      }
      // The deck the view is standing on, nearer than any layer: its seams
      // travel at depth 1 with the drift, so the roof and the city move as
      // one place rather than the city sliding behind a still floor.
      const step = playH * SEAM_STEP;
      drawRooftop(ctx, groundY, w, h, horizonY, {
        originX: -(stripPosition(screen, midX, w) % step),
        step,
      });
      drawDeckReflection(ctx, groundY, w, h);

      if (!still) frame = requestAnimationFrame(draw);
    };

    if (still) {
      // One frame once the layers are in, rather than a frame of flat fill.
      const poll = setInterval(() => {
        frame = requestAnimationFrame(draw);
      }, 250);
      const stop = setTimeout(() => clearInterval(poll), 4000);
      return () => {
        clearInterval(poll);
        clearTimeout(stop);
        cancelAnimationFrame(frame);
        window.removeEventListener('pointermove', onPointer);
      };
    }
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointer);
    };
  }, []);

  return <canvas ref={canvasRef} className="city-backdrop" aria-hidden="true" />;
};

export default CityBackdrop;
